import { useQuery } from '@apollo/client';
import { AddCircle } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Link, styled } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useCreateTemplateVersion } from '~/api/template-versions';
import { ListRowHeadingValue } from '~/components/list-row';
import { LoadingPane } from '~/components/loading-pane';
import { MetaItem, SettingsBody, SettingsData, SettingsHead } from '~/components/settings';
import { assert } from '~/lib/assert';
import { formatDate, formatInterval } from '~/lib/datetime';
import { toDataUri } from '~/lib/file';
import {
  TemplateVersionsPageDocument,
  type TemplateVersions__TemplateVersion as TemplateVersion,
} from '../queries/versions.generated';
import { Layout } from './components';

interface VersionPanelProps {
  readonly version: TemplateVersion;
}

const VersionPanel = ({ version }: VersionPanelProps) => {
  const { t } = useTranslation();

  return (
    <div>
      <SettingsHead>{version.version}</SettingsHead>
      <SettingsBody>
        <SettingsData className="last scroll no-space-between">
          <MetaItem className="first">
            <ListRowHeadingValue
              heading={t('pages.templates.versions.zip_file_label')}
              value={
                <Link href={version.uri} target="_blank" rel="noreferrer">
                  {version.version}.zip
                </Link>
              }
            />
          </MetaItem>
          <MetaItem>
            <ListRowHeadingValue
              heading={t('pages.templates.versions.checksum_label')}
              value={
                <Box
                  component="span"
                  sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                >
                  {version.checksum}
                </Box>
              }
            />
          </MetaItem>
          <MetaItem>
            <ListRowHeadingValue
              heading={t('common.labels.created')}
              value={formatDate(version.createdAt)}
            />
          </MetaItem>
          <MetaItem>
            <ListRowHeadingValue heading={t('common.labels.uses')} value={version.uses} />
          </MetaItem>
          <MetaItem>
            <ListRowHeadingValue
              heading={t('pages.templates.versions.default_duration_label')}
              value={
                version.defaultDuration
                  ? formatInterval(version.defaultDuration)
                  : t('common.labels.none')
              }
            />
          </MetaItem>
          <MetaItem>
            <ListRowHeadingValue
              heading={t('pages.templates.versions.aspect_ratio_label')}
              value={version.aspectRatio ?? t('common.labels.none')}
            />
          </MetaItem>
        </SettingsData>
      </SettingsBody>
    </div>
  );
};

const Input = styled('input')({ display: 'none' });

export const TemplateVersions = () => {
  const { t } = useTranslation();
  const params = useParams<{ templateId: string }>();
  const templateId = Number(params.templateId);

  const { data, loading } = useQuery(TemplateVersionsPageDocument, { variables: { templateId } });
  const versions = data?.template?.templateVersions.nodes ?? [];
  const canUpdate = data?.template?.canUpdate.value ?? false;

  const [createVersion, createState] = useCreateTemplateVersion();

  const [inputKey, setInputKey] = useState(Date.now());
  const [file, setFile] = useState<File>();

  const onCreate = async (event: React.MouseEvent) => {
    event.preventDefault();
    assert(file != null);
    const dataUrl = await toDataUri(file);
    await createVersion({ variables: { input: { templateId, dataUrl } } });
    setFile(undefined);
    setInputKey(Date.now());
    createState.reset();
  };

  return (
    <Layout>
      <LoadingPane in={loading && !data} size={40} thickness={4}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {canUpdate && (
            <Box
              sx={(theme) => ({
                alignItems: 'center',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                justifyContent: 'flex-end',
                [theme.breakpoints.down('sm')]: { justifyContent: 'center' },
              })}
            >
              {file && (
                <div>{t('pages.templates.versions.selected_file', { filename: file.name })}</div>
              )}
              <label htmlFor="contained-button-file">
                <Input
                  accept=".zip,application/zip"
                  disabled={createState.called}
                  id="contained-button-file"
                  type="file"
                  key={inputKey}
                  onChange={(event) => setFile(event.target.files?.item(0) ?? undefined)}
                />
                <Button variant="contained" color="primary" component="span">
                  {t('pages.templates.versions.upload_button')}
                </Button>
              </label>

              <LoadingButton
                color="primary"
                loading={createState.loading}
                variant="contained"
                loadingPosition="start"
                startIcon={<AddCircle />}
                disabled={!file || createState.called}
                onClick={(event) => void onCreate(event)}
              >
                {t('pages.templates.versions.create_button')}
              </LoadingButton>
            </Box>
          )}
          {versions.map((version) => (
            <VersionPanel version={version} key={version.id} />
          ))}
        </Box>
      </LoadingPane>
    </Layout>
  );
};
